import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Product } from '@setvi/shared/interfaces';

import { CustomerPreferencesInterface } from 'componentsV2/customer-preferences/interfaces';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import {
  getAskAiPrompts,
  getProductMetadataQuery,
  getSalesEnablementContentQuery
} from 'Services';

import { cleanSalesKit, extractMetadata, returnParseArray } from '../../utils';
import { useAnthropic } from '../use-anthropic';

interface ProductAskAiProps {
  product: Product;
  customerPreferences?: CustomerPreferencesInterface;
}

export interface ConversationProps {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  feedback: 'good' | 'bad' | null;
  loading?: boolean;
}

export const useProductAskAi = ({
  product,
  customerPreferences
}: ProductAskAiProps) => {
  const flags = useFlags();
  const { user } = useAppContext();

  const [questions, setQuestions] = useState<string[]>([]);
  const [conversation, setConversation] = useState<ConversationProps[]>([]);

  const params = useMemo(
    () => ({
      manufacturerName: product?.vendor?.name,
      manufacturerSku: user?.IsManufacturer
        ? product?.sku
        : product?.manufacturerSku,
      ...(product?.name && { productName: product?.name })
    }),
    [user, product]
  );

  const enabled = useMemo(() => {
    if (!params?.manufacturerName || !params?.manufacturerSku) return false;
    if (!user?.IsManufacturer && product?.sku === product?.manufacturerSku)
      return false;

    return true;
  }, [params, user, product]);

  const { data: metadata, isFetching: isMetadataFetching } = useQuery({
    ...getProductMetadataQuery(params),
    enabled: !!flags?.productAssistant && enabled,
    select: (data: any) => extractMetadata(data?.result)
  });

  const { data: salesKit, isInitialLoading: isSalesKitFetching } = useQuery({
    ...getSalesEnablementContentQuery(
      product?._id,
      product?.categories?.[0]?._id
    ),
    retry: false,
    enabled: !!product?._id && !!flags?.productAssistant,
    select: (res: any) => cleanSalesKit(res)
  });

  const { data: initPrompts, isFetching: areInitPromptsFetching } = useQuery({
    ...getAskAiPrompts(),
    enabled: !!flags?.productAssistant
  });

  const initalChatMessage = useMemo(
    () => ({
      role: 'assistant' as 'assistant' | 'user',
      content: initPrompts?.systemChat
        ?.replace('product_metadata', JSON.stringify(metadata))
        .replace('sales_kit', salesKit)
        .replace('customer_preferences', JSON.stringify(customerPreferences))
    }),
    [initPrompts?.systemChat, metadata, salesKit, customerPreferences]
  );

  const initalQuestionMessage = useMemo(
    () => ({
      role: 'assistant' as 'assistant' | 'user',
      content: initPrompts?.systemQuestions
        ?.replace('product_metadata', JSON.stringify(metadata))
        .replace('sales_kit', salesKit)
        .replace('customer_preferences', JSON.stringify(customerPreferences))
    }),
    [initPrompts?.systemQuestions, metadata, salesKit, customerPreferences]
  );

  const { triggerChat: getQuestions, loading: questionsLoading } = useAnthropic(
    {}
  );
  const { triggerChat, response, loading, streamLoading } = useAnthropic({});

  const fetchQuestions = async () => {
    const q = await getQuestions({
      stream: false,
      messages: [
        initalQuestionMessage,
        {
          role: 'user',
          content: initPrompts?.questionsPrompt
        }
      ]
    });

    try {
      const res = returnParseArray(q);
      setQuestions(prev => res || prev);
      //  if (res?.length) setInitLoading(false);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  const initLoading = useMemo(
    () =>
      isSalesKitFetching ||
      areInitPromptsFetching ||
      isMetadataFetching ||
      (!conversation?.length ? questionsLoading : false),
    [
      isSalesKitFetching,
      isMetadataFetching,
      areInitPromptsFetching,
      questionsLoading,
      conversation
    ]
  );

  useEffect(() => {
    if (
      !isSalesKitFetching &&
      !isMetadataFetching &&
      !areInitPromptsFetching &&
      !!flags?.productAssistant
    )
      fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSalesKitFetching,
    isMetadataFetching,
    areInitPromptsFetching,
    flags?.productAssistant
  ]);

  const onSubmit = async (val: string) => {
    setConversation(prev => [
      ...prev,
      {
        id: conversation?.length,
        feedback: null,
        role: 'user',
        content: val,
        loading: false
      },
      {
        id: conversation?.length + 1,
        feedback: null,
        role: 'assistant',
        content: '',
        loading: true
      }
    ]);

    const cleanConvo = conversation?.map(({ role, content }) => ({
      role,
      content
    }));
    const history = [
      ...cleanConvo,
      { role: 'user' as 'user' | 'assistant', content: val }
    ];

    triggerChat({
      stream: true,
      messages: [initalChatMessage, ...history]
    });
  };

  useEffect(() => {
    setConversation(prev =>
      prev.map((item, index) =>
        index === prev.length - 1
          ? { ...item, content: response, loading }
          : item
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, loading]);

  return {
    loading: streamLoading,
    initLoading,
    questions,
    conversation,
    onSubmit,
    setConversation
  };
};
